import React, { useEffect, useMemo, useState } from 'react'
import i18n from 'simple-react-i18n'
import PropTypes from 'prop-types'
import NumberField from '../../../../components/forms/NumberField'
import RadioButtons from '../../../../components/forms/RadioButtons'
import useUpdateEffect from '../../../../utils/customHook/useUpdateEffect'
import { ONE_MONTH, ONE_WEEK, ONE_YEAR, OTHER, CUSTOM } from 'alerting/constants/ChartFollowContants'
import { Card, CardContent, Grid, Stack } from '@mui/material'
import Input from 'components/forms/Input'
import { StyledFieldSet, StyledLegend } from 'components/StyledElements'
import Checkbox from 'components/forms/Checkbox'
import { AUTO_Y_SCALE, MANUAL_Y_SCALE } from 'quality/constants/ChartConstant'
import { IconButtonMUI } from 'components/styled/Buttons'
import { THRESHOLD_COLORS_CODES } from 'utils/ColorUtil'
import { CardTitle } from 'components/card/NewCard'
import { hasValue } from 'utils/NumberUtil'

const EMPTY_OPTIONS = {}

const getInitialYChoice = options => options.yChoice ?? (hasValue(options.minY) || hasValue(options.maxY) ? MANUAL_Y_SCALE : AUTO_Y_SCALE)

const SimpleOptionsToolPanel = ({
    showTitle = true,
    updateState = () => {},
    startDate = null,
    endDate = null,
    defaultOptions = EMPTY_OPTIONS,
}) => {
    const [graphicTitle, setGraphicTitle] = useState(defaultOptions.graphicTitle || '')
    const [minY, setMinY] = useState(defaultOptions.minY)
    const [maxY, setMaxY] = useState(defaultOptions.maxY)
    const [intervalChoice, setIntervalChoice] = useState(defaultOptions.intervalChoice || OTHER)
    const [intervalNb, setIntervalNb] = useState(defaultOptions.intervalNb || 5)
    const [yIntervalNb, setYIntervalNb] = useState(defaultOptions.yIntervalNb) // nb d'intervalles de l'axe Y (échelle manuelle)
    const [yChoice, setYChoice] = useState(getInitialYChoice(defaultOptions))
    const [showXSplitLines, setShowXSplitLines] = useState(defaultOptions.showXSplitLines ?? true)
    const [showYSplitLines, setShowYSplitLines] = useState(defaultOptions.showYSplitLines ?? true)

    useEffect(() => {
        setGraphicTitle(defaultOptions.graphicTitle ?? '')
        setMinY(defaultOptions.minY)
        setMaxY(defaultOptions.maxY)
        setIntervalChoice(defaultOptions.intervalChoice ?? OTHER)
        setIntervalNb(defaultOptions.intervalNb ?? 5)
        setYIntervalNb(defaultOptions.yIntervalNb)
        setYChoice(getInitialYChoice(defaultOptions))
        setShowXSplitLines(defaultOptions.showXSplitLines ?? true)
        setShowYSplitLines(defaultOptions.showYSplitLines ?? true)
    }, [defaultOptions])

    useUpdateEffect(() => {
        setIntervalChoice(OTHER)
    }, [startDate, endDate])

    const onCancel = () => {
        setGraphicTitle('')
        setMinY()
        setMaxY()
        setIntervalChoice(OTHER)
        setIntervalNb(5)
        setYIntervalNb()
        setYChoice(AUTO_Y_SCALE)
        setShowXSplitLines(true)
        setShowYSplitLines(true)
        updateState({
            graphicTitle: '',
            minY: null,
            maxY: null,
            yIntervalNb: null,
            intervalChoice: OTHER,
            intervalNb: 5,
            showXSplitLines: true,
            showYSplitLines: true,
        })
    }

    const onValidate = () => updateState({
        graphicTitle,
        minY,
        maxY,
        yIntervalNb,
        intervalChoice,
        showXSplitLines,
        showYSplitLines,
        intervalNb,
    })

    const intervalChoices = useMemo(() => {
        if (startDate && endDate && (endDate - startDate > 3600*1000*24*365.5*2)) { // si supérieur à 2 ans
            return [
                { code: OTHER, name: i18n.automatic },
                { code: ONE_YEAR, name: i18n.perYear },
                { code: CUSTOM, name: i18n.choice },
            ]
        }
        return [
            { code: OTHER, name: i18n.automatic },
            { code: ONE_WEEK, name: i18n.perWeek },
            { code: ONE_MONTH, name: i18n.perMonth },
            { code: ONE_YEAR, name: i18n.perYear },
            { code: CUSTOM, name: i18n.choice },
        ]
    }, [startDate, endDate])

    const yMode = useMemo(() => [
        {
            code: AUTO_Y_SCALE,
            name: i18n.automatic,
        }, {
            code: MANUAL_Y_SCALE,
            name: i18n.manual,
        },
    ], [])

    const minMaxDisabled = yChoice !== MANUAL_Y_SCALE

    useEffect(() => {
        if (yChoice === AUTO_Y_SCALE) {
            setMinY(undefined)
            setMaxY(undefined)
            setYIntervalNb(undefined)
        }
    }, [yChoice])

    return (
        <Card className='margin-top-1' >
            <CardTitle title={i18n.chartOptions} />
            <CardContent>
                <Stack sx={{ padding: '10px' }}>
                    {showTitle && (
                        <Input
                            value={graphicTitle}
                            title={i18n.title}
                            onChange={setGraphicTitle}
                        />
                    )}
                    <StyledFieldSet className='padding-1'>
                        <StyledLegend>{i18n.yScale}</StyledLegend>
                        <Checkbox
                            componentClassName='margin-left-1 padding-top-1 padding-bottom-1'
                            checked={showYSplitLines}
                            label={`${i18n.show} / ${i18n.toggle} ${i18n.Ygrid}`}
                            onChange={setShowYSplitLines}
                        />
                        <RadioButtons
                            title={i18n.chartScale}
                            elements={yMode}
                            selected={yChoice}
                            onChange={setYChoice}
                            className='padding-bottom-1'
                        />
                        <NumberField
                            title={i18n.min}
                            value={minY}
                            onChange={setMinY}
                            disabled={minMaxDisabled}
                            floatValue
                        />
                        <NumberField
                            title={i18n.max}
                            value={maxY}
                            onChange={setMaxY}
                            disabled={minMaxDisabled}
                            floatValue
                        />
                        <NumberField
                            title={i18n.intervalCount}
                            value={yIntervalNb}
                            onChange={setYIntervalNb}
                            disabled={minMaxDisabled}
                            min={1}
                        />
                    </StyledFieldSet>
                    <StyledFieldSet className='padding-1'>
                        <StyledLegend>{i18n.xScale}</StyledLegend>
                        <Checkbox
                            componentClassName='margin-left-1 padding-top-1 padding-bottom-1'
                            checked={showXSplitLines}
                            label={`${i18n.show} / ${i18n.toggle} ${i18n.Xgrid}`}
                            onChange={setShowXSplitLines}
                        />
                        <RadioButtons
                            elements={intervalChoices}
                            selected={intervalChoice}
                            onChange={setIntervalChoice}
                            colOption={6}
                        />
                        <NumberField value={intervalNb} onChange={setIntervalNb} title={i18n.intervalCount} disabled={intervalChoice !== CUSTOM} min={1}/>
                    </StyledFieldSet>
                    <Grid container size={12} justifyContent='center' sx={{ marginTop: '10px' }}>
                        <IconButtonMUI
                            tooltip={i18n.cancel}
                            onClick={onCancel}
                            icon='cancel'
                            color={THRESHOLD_COLORS_CODES.RED}
                            sx={{ marginRight: '0.75rem' }}
                        />
                        <IconButtonMUI
                            tooltip={i18n.register}
                            onClick={onValidate}
                            icon='save'
                            sx={{ marginLeft: '0.75rem' }}
                        />
                    </Grid>
                </Stack>
            </CardContent>
        </Card>
    )
}

SimpleOptionsToolPanel.propTypes = {
    showTitle: PropTypes.bool,
    updateState: PropTypes.func,
    startDate: PropTypes.number,
    endDate: PropTypes.number,
    defaultOptions: PropTypes.shape({
        graphicTitle: PropTypes.string,
        minY: PropTypes.number,
        maxY: PropTypes.number,
        intervalChoice: PropTypes.string,
        intervalNb: PropTypes.number,
        yIntervalNb: PropTypes.number,
        yChoice: PropTypes.number,
        showXSplitLines: PropTypes.bool,
        showYSplitLines: PropTypes.bool,
    }),
}

export default SimpleOptionsToolPanel